// src/context/AuthContext.jsx
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { authService } from "../api/services";
import { TokenStore } from "../api/client";
import { SUPPORTED_LANGUAGES } from "../i18n";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem("ms_user");
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  // Verificar sesión ao arrancar
  useEffect(() => {
    const token = TokenStore.get();
    if (!token) { setLoading(false); return; }

    authService.me()
      .then(u => {
        setUser(u);
        localStorage.setItem("ms_user", JSON.stringify(u));
      })
      .catch(() => {
        TokenStore.clear();
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const data = await authService.login(email, password);
    TokenStore.set(data.accessToken, data.refreshToken);
    localStorage.setItem("ms_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (formData) => {
    const data = await authService.register(formData);
    TokenStore.set(data.accessToken, data.refreshToken);
    localStorage.setItem("ms_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  }, []);

  // X-4.1 · login con Google idToken (consume X-3.2)
  const loginWithGoogle = useCallback(async (idToken) => {
    const data = await authService.googleSignIn(idToken);
    TokenStore.set(data.accessToken, data.refreshToken);
    localStorage.setItem("ms_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    TokenStore.clear();
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const u = await authService.me();
    setUser(u);
    localStorage.setItem("ms_user", JSON.stringify(u));
    return u;
  }, []);

  // Aplica o idioma preferido do usuario logueado. Se non está definido,
  // o idioma queda no detectado polo browser (cookie / Accept-Language).
  // Este efecto corre cada vez que o user cambia (login, /me refresh).
  const { i18n } = useTranslation();
  useEffect(() => {
    const lang = user?.preferredLanguage;
    if (!lang || !SUPPORTED_LANGUAGES.includes(lang)) return;
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [user?.preferredLanguage, i18n]);

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithGoogle, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de AuthProvider");
  return ctx;
};
